import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type {
  AccountingMode,
  AccountingSelection,
  Ansicht,
} from '@/features/reports/lib/accounting-period'
import type { ClosingCloseOption } from '@/features/reports/api/use-closing-report-query'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useClosesQuery } from '@/features/reports/api/use-closing-report-query'
import { ANSICHTEN, shiftMonth } from '@/features/reports/lib/accounting-period'
import {
  formatShortDateTime,
  formatWeekdayDateTime,
} from '@/features/reports/lib/format'

const MODES: Array<AccountingMode> = ['tag', 'monat', 'zeitraum']

const INPUT =
  'h-9 rounded-md border border-input bg-background px-2 text-sm tabular-nums'

/**
 * The Buchhaltung period control (CHG-2026-10-accounting-close-periods, D1 and § UI).
 *
 * Every period is bounded by Tagesabschlüsse, so the control never offers a calendar day: Tag picks
 * one close from a month's list, Monat steps through months, Zeitraum picks the close after which
 * and the close up to which. The lists come from the cheap closes endpoint, newest first.
 *
 * The month and the close are native inputs, deliberately — `type="month"` and `<select>` give the
 * browser's own keyboard entry, which is what somebody going through a month of closes wants.
 */
export function AccountingPeriodControl({
  mode,
  selection,
  restaurantId,
  viewMonth,
  fromMonth,
  thisMonth,
  onModeChange,
  onSelect,
}: {
  /** The mode on screen; may run ahead of {@code selection} while a Tag or Zeitraum is being picked. */
  mode: AccountingMode
  selection: AccountingSelection
  restaurantId: number | null
  /** The month the pickers open on: the month of the period's last close, or the selected month. */
  viewMonth: string
  /** The month of the period's start, where the Zeitraum's "nach Abschluss" picker opens. */
  fromMonth: string
  thisMonth: string
  onModeChange: (mode: AccountingMode) => void
  onSelect: (selection: AccountingSelection) => void
}) {
  const { t } = useTranslation('reports')

  return (
    <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-center">
      <ToggleGroup
        type="single"
        size="sm"
        value={mode}
        onValueChange={(next) => {
          if (next) onModeChange(next as AccountingMode)
        }}
        aria-label={t('accounting.periodMode')}
      >
        {MODES.map((m) => (
          <ToggleGroupItem key={m} value={m}>
            {t(`accounting.modes.${m}`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {mode === 'monat' ? (
        <Stepper
          onBack={() =>
            onSelect({ mode: 'monat', month: shiftMonth(viewMonth, -1) })
          }
          onForward={() =>
            onSelect({ mode: 'monat', month: shiftMonth(viewMonth, 1) })
          }
          forwardDisabled={viewMonth >= thisMonth}
          backLabel={t('accounting.monthBack')}
          forwardLabel={t('accounting.monthForward')}
        >
          <input
            type="month"
            value={viewMonth}
            max={thisMonth}
            aria-label={t('accounting.modes.monat')}
            onChange={(event) =>
              event.target.value &&
              onSelect({ mode: 'monat', month: event.target.value })
            }
            className={INPUT}
          />
        </Stepper>
      ) : null}

      {mode === 'tag' ? (
        <ClosePicker
          label={t('accounting.pickClose')}
          restaurantId={restaurantId}
          initialMonth={viewMonth}
          thisMonth={thisMonth}
          value={selection.mode === 'tag' ? selection.close : null}
          onChange={(ref) => onSelect({ mode: 'tag', close: ref })}
        />
      ) : null}

      {mode === 'zeitraum' ? (
        <RangePicker
          restaurantId={restaurantId}
          selection={selection}
          fromMonth={fromMonth}
          untilMonth={viewMonth}
          thisMonth={thisMonth}
          onSelect={onSelect}
        />
      ) : null}
    </div>
  )
}

/** "nach Abschluss" and "bis Abschluss": whole closes, the first exclusive, the second inclusive. */
function RangePicker({
  restaurantId,
  selection,
  fromMonth,
  untilMonth,
  thisMonth,
  onSelect,
}: {
  restaurantId: number | null
  selection: AccountingSelection
  fromMonth: string
  untilMonth: string
  thisMonth: string
  onSelect: (selection: AccountingSelection) => void
}) {
  const { t } = useTranslation('reports')
  const selected = selection.mode === 'zeitraum' ? selection : null
  // Half a Zeitraum is kept here until the other end is picked; only a whole one is asked for.
  const [after, setAfter] = useState<string | null>(selected?.after ?? null)
  const [until, setUntil] = useState<string | null>(selected?.until ?? null)
  useEffect(() => {
    setAfter(selected?.after ?? null)
    setUntil(selected?.until ?? null)
  }, [selected?.after, selected?.until])

  const pick = (nextAfter: string | null, nextUntil: string | null) => {
    setAfter(nextAfter)
    setUntil(nextUntil)
    if (nextAfter && nextUntil) {
      onSelect({ mode: 'zeitraum', after: nextAfter, until: nextUntil })
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <ClosePicker
        label={t('accounting.rangeAfter')}
        restaurantId={restaurantId}
        initialMonth={fromMonth}
        thisMonth={thisMonth}
        value={after}
        onChange={(ref) => pick(ref, until)}
      />
      <ClosePicker
        label={t('accounting.rangeUntil')}
        restaurantId={restaurantId}
        initialMonth={untilMonth}
        thisMonth={thisMonth}
        value={until}
        onChange={(ref) => pick(after, ref)}
      />
    </div>
  )
}

/** A month and one of its closes, newest first. */
function ClosePicker({
  label,
  restaurantId,
  initialMonth,
  thisMonth,
  value,
  onChange,
}: {
  label: string
  restaurantId: number | null
  initialMonth: string
  thisMonth: string
  value: string | null
  onChange: (ref: string) => void
}) {
  const { t } = useTranslation('reports')
  const [month, setMonth] = useState(initialMonth)
  useEffect(() => setMonth(initialMonth), [initialMonth])
  const closes = useClosesQuery(restaurantId, month)
  const options: Array<ClosingCloseOption> = closes.data?.closes ?? []
  const known = options.some((o) => o.ref === value)

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type="month"
        value={month}
        max={thisMonth}
        aria-label={`${label} · ${t('accounting.modes.monat')}`}
        onChange={(event) => event.target.value && setMonth(event.target.value)}
        className={INPUT}
      />
      <select
        aria-label={label}
        value={known && value ? value : ''}
        onChange={(event) => event.target.value && onChange(event.target.value)}
        className={`${INPUT} min-w-44`}
      >
        <option value="" disabled>
          {options.length === 0 && !closes.isLoading
            ? t('accounting.noClosesInMonth')
            : t('accounting.pickClosePlaceholder')}
        </option>
        {options.map((o) => (
          <option key={o.ref} value={o.ref}>
            {formatWeekdayDateTime(o.closedAt)}
            {o.daySpan > 1
              ? ` · ${t('accounting.days', { count: o.daySpan })}`
              : ''}
            {o.from == null ? ` · ${t('accounting.sinceStart')}` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

/** A value between two step buttons. */
function Stepper({
  children,
  onBack,
  onForward,
  forwardDisabled,
  backLabel,
  forwardLabel,
}: {
  children: React.ReactNode
  onBack: () => void
  onForward: () => void
  forwardDisabled: boolean
  backLabel: string
  forwardLabel: string
}) {
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={onBack}
        aria-label={backLabel}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      {children}
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={onForward}
        disabled={forwardDisabled}
        aria-label={forwardLabel}
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  )
}

/** "Ansicht: Übersicht | Clemens" — both always show the same Standort and period. */
export function AnsichtSwitch({
  value,
  onChange,
}: {
  value: Ansicht
  onChange: (ansicht: Ansicht) => void
}) {
  const { t } = useTranslation('reports')
  return (
    <div className="flex items-center gap-2" data-print-hide>
      <span className="text-sm text-muted-foreground">
        {t('accounting.ansicht.label')}
      </span>
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
        value={value}
        onValueChange={(next) => {
          if (next) onChange(next as Ansicht)
        }}
        aria-label={t('accounting.ansicht.label')}
      >
        {ANSICHTEN.map((a) => (
          <ToggleGroupItem key={a} value={a}>
            {t(`accounting.ansicht.${a}`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}

/** "22 Tagesabschlüsse · 31.08. 16:05 → 30.09. 14:42" — the window the period covers. */
export function windowLine(
  t: (key: string, options?: Record<string, unknown>) => string,
  period: { closeCount: number; from: string | null; to: string | null },
): string {
  if (period.closeCount === 0 || period.to == null) return ''
  return t('accounting.windowLine', {
    count: period.closeCount,
    from:
      period.from == null
        ? t('accounting.sinceStart')
        : formatShortDateTime(period.from),
    to: formatShortDateTime(period.to),
  })
}
